import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { setModal } from "actions"
import { emit } from "actions/socket"
import { Buttons, Inputs } from "components"
import { Alerts } from "utilities"
import "./styles.scss"

const Notifications = () => {
    const dispatch = useDispatch()
    const [searchString, setSearchString] = useState('')
    const [selectedClients, setSelectedClients] = useState([])
    const [sendToAll, setSendToAll] = useState(false)
    const [title, setTitle] = useState('')
    const [text, setText] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [isSearching, setIsSearching] = useState(false)

    // Debounced search
    useEffect(() => {
        if (searchString.length < 3) {
            setSearchResults([])
            return
        }

        setIsSearching(true)
        const timeoutId = setTimeout(() => {
            emit({
                action: 'client/search',
                payload: { searchString },
                onSuccess: (response) => {
                    setSearchResults(response?.clients || [])
                    setIsSearching(false)
                },
                onFailed: () => {
                    setSearchResults([])
                    setIsSearching(false)
                }
            })
        }, 500)

        return () => clearTimeout(timeoutId)
    }, [searchString])

    const handleToggleClient = (client) => {
        if (selectedClients.find(c => c._id === client._id)) {
            setSelectedClients(selectedClients.filter(c => c._id !== client._id))
        } else {
            setSelectedClients([...selectedClients, client])
        }
    }

    const handleRemoveClient = (clientId) => {
        setSelectedClients(selectedClients.filter(c => c._id !== clientId))
    }

    const handleSend = () => {
        const clientIds = sendToAll ? [] : selectedClients.map(c => c._id)

        emit({
            action: 'notification/sendCustom',
            payload: {
                title,
                text,
                saveInDb: true,
                clientIds
            },
            startLoading: true,
            stopLoading: true,
            onSuccess: (response) => {
                Alerts.success({
                    title: 'Известието е изпратено!',
                    text: `Изпратено до ${response.totalClients} клиента${response.totalClients !== 1 ? '' : ''}. Успешно: ${response.sent}, Неуспешно: ${response.failed}`
                })
                
                setSearchString('')
                setSelectedClients([])
                setTitle('')
                setText('')
                setSendToAll(false)
            }
        })
    }

    return <div className="screen-notifications-container">
        <div className="screen-notifications-header">
            <div className="header-left">
                <h1>Изпращане на известия</h1>
                <p>Изпратете персонализирани push известия до вашите клиенти</p>
            </div>
            <div className="header-actions">
                <Buttons.Raised
                    text="Изпрати известие"
                    onClick={handleSend}
                    disabled={!title || !text}
                />
                <button 
                    className="btn-clear"
                    onClick={() => {
                        setSearchString('')
                        setSelectedClients([])
                        setTitle('')
                        setText('')
                        setSendToAll(false)
                    }}
                >
                    Изчисти
                </button>
            </div>
        </div>

        <div className="screen-notifications-content">
            <div className="content-left">
                <div className="notification-section unified-section">
                    <div className="form-content">
                        {/* Recipients Section */}
                        <div className="form-section">
                            <div className="section-title-row">
                                <h3 className="section-title">Избор на получатели</h3>
                                <button 
                                    className="btn-preview"
                                    disabled={!title || !text}
                                    onClick={() => dispatch(setModal({ 
                                        isOpen: true, 
                                        type: 'NotificationPreview',
                                        props: { title, text }
                                    }))}
                                >
                                    Преглед
                                </button>
                            </div>
                            
                            <div className="recipient-type-selector">
                                <div 
                                    className={`recipient-option ${sendToAll ? 'active' : ''}`}
                                    onClick={() => setSendToAll(true)}
                                >
                                    <div className="radio-circle">
                                        {sendToAll && <div className="radio-dot" />}
                                    </div>
                                    <span className="option-label">Всички</span>
                                </div>
                                <div 
                                    className={`recipient-option ${!sendToAll ? 'active' : ''}`}
                                    onClick={() => setSendToAll(false)}
                                >
                                    <div className="radio-circle">
                                        {!sendToAll && <div className="radio-dot" />}
                                    </div>
                                    <span className="option-label">Избери получатели</span>
                                </div>
                            </div>

                            {!sendToAll && (
                                <div className="client-selection">
                                    <div className="multi-select-input">
                                        {selectedClients.map(client => (
                                            <div key={client._id} className="input-tag">
                                                <span className="tag-name">{client.fullName}</span>
                                                <span 
                                                    className="tag-remove" 
                                                    onClick={() => handleRemoveClient(client._id)}
                                                >
                                                    ×
                                                </span>
                                            </div>
                                        ))}
                                        <input
                                            type="text"
                                            className="multi-select-search"
                                            value={searchString}
                                            onChange={(e) => setSearchString(e.target.value)}
                                            placeholder={selectedClients.length === 0 ? "Търсене по име, телефон или имейл..." : ""}
                                        />
                                    </div>

                                    {searchResults.length > 0 && (
                                        <div className="search-results">
                                            <div className="results-header">
                                                <span>{searchResults.length} резултата</span>
                                            </div>
                                            {searchResults.map(client => (
                                                <div 
                                                    key={client._id} 
                                                    className={`search-result-item ${selectedClients.find(c => c._id === client._id) ? 'selected' : ''}`}
                                                    onClick={() => handleToggleClient(client)}
                                                >
                                                    <div className="result-checkbox">
                                                        <div className="checkbox-inner">
                                                            {selectedClients.find(c => c._id === client._id) && (
                                                                <div className="icon icon-check" />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="result-info">
                                                        <p className="result-name">{client.fullName}</p>
                                                        <p className="result-details">
                                                            <span>{client.phoneNumber}</span>
                                                            {client.email && (
                                                                <>
                                                                    <span className="dot">•</span>
                                                                    <span>{client.email}</span>
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Message Content Section */}
                        <div className="form-section">
                            <h3 className="section-title">Съдържание на известието</h3>
                            
                            <div className="input-wrapper">
                                <label className="input-label">
                                    Заглавие
                                    <span className="required">*</span>
                                </label>
                                <Inputs.Text
                                    value={title}
                                    onChange={({ target: { value } }) => setTitle(value)}
                                    placeholder="Напр. Специална оферта!"
                                />
                            </div>

                            <div className="input-wrapper">
                                <label className="input-label">
                                    Съобщение
                                    <span className="required">*</span>
                                </label>
                                <Inputs.Comments
                                    value={text}
                                    onChange={({ target: { value } }) => setText(value)}
                                    placeholder="Напр. Получете 20% отстъпка при следващото измиване..."
                                    rows={6}
                                />
                                <div className="input-footer">
                                    <span className="char-counter">{text.length} символа</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-right">
                <div className="recipients-panel">
                    <div className="recipients-header">
                        <h3>Получатели</h3>
                        <div className="recipients-count">
                            <span className="count-number">{sendToAll ? 'Всички' : selectedClients.length}</span>
                            <span className="count-label">{sendToAll ? 'клиенти' : 'избрани'}</span>
                        </div>
                    </div>
                    <div className="recipients-content">
                        {sendToAll ? (
                            <div className="all-recipients-message">
                                <p>Известието ще бъде изпратено до всички регистрирани клиенти в MobiWash платформата.</p>
                            </div>
                        ) : selectedClients.length > 0 ? (
                            <div className="recipients-list">
                                {selectedClients.map(client => (
                                    <div key={client._id} className="recipient-item">
                                        <div className="recipient-info">
                                            <p className="recipient-name">{client.fullName}</p>
                                            <p className="recipient-contact">{client.phoneNumber}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-recipients-message">
                                <p>Изберете клиенти от търсенето или активирайте "Изпращане до всички клиенти"</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
}

export default Notifications
